import {helper} from "./helper.js";

/**
 * Fonctions utilitaires partagées entre LaveuseTrackingPage et LaveuseTripsPage
 * pour le traitement des données de route et de télématique.
 */
export const laveuseHelper = {

    /**
     * Calcule les engineTimes (ignitionOn / ignitionOff) à partir de la route.
     * Premier point = ignitionOn, dernier point = ignitionOff.
     * @param {Array} route - Tableau de points GPS avec {latitude, longitude, speed, time}
     * @returns {{ignitionOn: string|null, ignitionOff: string|null}}
     */
    getEngineTimes: (route) => {
        if (!route || route.length === 0) return {ignitionOn: null, ignitionOff: null};
        return {
            ignitionOn: route[0].time ?? null,
            ignitionOff: route[route.length - 1].time ?? null
        };
    },

    /**
     * Calcule un résumé de trajet à partir des points de route.
     * On ne compte que la plage "en mouvement" : du premier point avec speed > 0
     * au dernier point avec speed > 0, pour exclure les pings nocturnes / stationnement.
     * @param {Array} route - Tableau de points GPS avec {latitude, longitude, speed, time}
     * @returns {object|null}
     */
    getRouteSummary: (route) => {
        if (!route || route.length === 0) return null;

        // Trouver le premier et dernier index où speed > 0
        let firstMovingIdx = -1;
        let lastMovingIdx = -1;
        for (let i = 0; i < route.length; i++) {
            if (route[i].speed > 0) {
                if (firstMovingIdx === -1) firstMovingIdx = i;
                lastMovingIdx = i;
            }
        }

        // Si aucun point en mouvement, pas de trajet
        if (firstMovingIdx === -1) {
            return {
                distanceKm: "0.00",
                durationMinutes: 0,
                avgSpeed: null,
                startTime: route[0].time,
                endTime: route[route.length - 1].time
            };
        }

        // Distance totale uniquement sur la plage en mouvement
        let totalDistance = 0;
        for (let i = firstMovingIdx + 1; i <= lastMovingIdx; i++) {
            const d = helper.getDistanceMeters(
                route[i - 1].latitude, route[i - 1].longitude,
                route[i].latitude, route[i].longitude
            );
            if (d != null) totalDistance += d;
        }

        // Durée entre premier point en mouvement et dernier point en mouvement
        const startTime = route[firstMovingIdx].time ? new Date(route[firstMovingIdx].time) : null;
        const endTime = route[lastMovingIdx].time ? new Date(route[lastMovingIdx].time) : null;
        const durationMs = (startTime && endTime) ? (endTime - startTime) : null;
        const durationMinutes = durationMs != null ? Math.round(durationMs / 60000) : null;

        // Vitesse moyenne
        const avgSpeed = (durationMs && durationMs > 0 && totalDistance > 0)
            ? ((totalDistance / 1000) / (durationMs / 3600000)).toFixed(1)
            : null;

        return {
            distanceKm: (totalDistance / 1000).toFixed(2),
            durationMinutes,
            avgSpeed,
            startTime: route[firstMovingIdx].time,
            endTime: route[lastMovingIdx].time
        };
    },

    /**
     * Formate une durée en minutes en texte lisible (ex: "1h23min", "45min")
     * @param {number|null} minutes
     * @returns {string}
     */
    getReadableDuration: (minutes) => {
        if (minutes == null) return "---";
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return hours > 0 ? `${hours}h${mins}min` : `${mins}min`;
    },
};
